/**
 * @fileoverview Results Table
 * @description Format-aware table of scenario or evaluator results.
 */

import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Eye } from 'lucide-react';
import { ScoreChip } from './common';
import { BAND_COLORS } from '../constants';

/**
 * Render a metric value with its proportional bar.
 * @param {Object} props - Normalized metric value between 0 and 1.
 * @returns {JSX.Element} Metric cell content.
 */
function MetricBar({ value }) {
  if (value === null || value === undefined) {
    return <span className="aier-metric-empty">—</span>;
  }
  const percent = Math.min(100, Math.max(0, value * 100));
  return (
    <Box className="aier-metric-bar">
      <span className="aier-metric-bar-value">{value.toFixed(2)}</span>
      <Box className="aier-metric-bar-track">
        <Box className="aier-metric-bar-fill" style={{ width: `${percent}%` }} />
      </Box>
    </Box>
  );
}

/**
 * Render the evaluation results table.
 * @param {Object} props - Rows, layout flag, and row selection handler.
 * @returns {JSX.Element} Results table.
 */
export function ResultsTable({ rows, isScenarioLayout, onSelectRow }) {
  return (
    <>
      <TableContainer className="aier-table-container">
        <Table size="small" className="aier-table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>{isScenarioLayout ? 'Scenario' : 'Report field'}</TableCell>
              {isScenarioLayout ? (
                <>
                  <TableCell align="center">Goal coverage</TableCell>
                  <TableCell align="center">Response quality</TableCell>
                  <TableCell align="center">Efficiency</TableCell>
                  <TableCell align="center">Overall</TableCell>
                  <TableCell align="center">Result</TableCell>
                </>
              ) : (
                <>
                  <TableCell>AI evaluator</TableCell>
                  <TableCell>Framework</TableCell>
                  <TableCell>Approach</TableCell>
                  <TableCell align="center">Evaluation score</TableCell>
                </>
              )}
              <TableCell align="center">Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover className={`aier-row aier-row--${row.band}`}>
                <TableCell className="aier-cell-index">{row.report}</TableCell>
                <TableCell className="aier-cell-field">{row.field}</TableCell>
                {isScenarioLayout ? (
                  <>
                    <TableCell align="center" className="aier-cell-metric">
                      <MetricBar value={row.metrics ? row.metrics.goalCoverage : null} />
                    </TableCell>
                    <TableCell align="center" className="aier-cell-metric">
                      <MetricBar value={row.metrics ? row.metrics.responseQuality : null} />
                    </TableCell>
                    <TableCell align="center" className="aier-cell-metric">
                      <MetricBar value={row.metrics ? row.metrics.efficiency : null} />
                    </TableCell>
                    <TableCell align="center"><ScoreChip score={row.score} band={row.band} /></TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        className="aier-result-pill"
                        label={row.passed ? 'Passed' : 'Failed'}
                        sx={{
                          color: row.passed ? BAND_COLORS.green : BAND_COLORS.red,
                          backgroundColor: `${row.passed ? BAND_COLORS.green : BAND_COLORS.red}14`,
                          borderColor: `${row.passed ? BAND_COLORS.green : BAND_COLORS.red}33`,
                        }}
                      />
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{row.evaluator}</TableCell>
                    <TableCell className="aier-cell-muted">{row.framework}</TableCell>
                    <TableCell className="aier-cell-muted">{row.approach}</TableCell>
                    <TableCell align="center"><ScoreChip score={row.score} band={row.band} /></TableCell>
                  </>
                )}
                <TableCell align="center">
                  <Tooltip title="View detail">
                    <IconButton
                      size="small"
                      onClick={() => onSelectRow(row)}
                      aria-label={`View detail for ${isScenarioLayout ? row.field : row.evaluator}`}
                    >
                      <Eye size={16} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {rows.length === 0 && (
        <Box className="aier-no-results">
          <Typography color="text.secondary">
            No {isScenarioLayout ? 'scenarios' : 'evaluators'} match the current search and filter.
          </Typography>
        </Box>
      )}
    </>
  );
}

export default ResultsTable;
